import { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  reportCollectionsFilterSelector,
  reportFilterCountryCodeSelector,
  reportFilterSelector,
  reportPettyCashDifferenceByPaymentTypeSelector,
  downloadReportPdf,
} from '../../state';
import { PettyCashDifferenceByPaymentType } from '../../types';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../DataGridFlexSales';
import {
  getAccountingInformationReportPdfFilters,
  getRecaPdfParameters,
} from '../AccountingInformationReportDataGrids/getPdfFilters';
import handlerFactory, { PettyCashDifferenceByPaymentTypeDataGridContext } from './handlerFactory';

const DataGridConfig = () => {
  const { handler } = useDataGridFlexSales<
    PettyCashDifferenceByPaymentType,
    PettyCashDifferenceByPaymentTypeDataGridContext
  >();
  const { t } = useTranslation();
  const title = {
    title: t('petty-cash-difference-by-payment-type.file-title'),
    tableTitle: t('petty-cash-difference-by-payment-type.title'),
  };
  const country = useSelector(reportFilterCountryCodeSelector);
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);

  const { loading, data } = useSelector(reportPettyCashDifferenceByPaymentTypeSelector);

  const gridContext = useMemo(
    () => ({
      t,
      country,
    }),
    [t, country]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    title,
    loading,
    filters: reportCollectionsFilters,
  });

  return null;
};

const DataGridExportConfig = () => {
  const { handler } = useDataGridFlexSales<
    PettyCashDifferenceByPaymentType,
    PettyCashDifferenceByPaymentTypeDataGridContext
  >();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const reportFilters = useSelector(reportFilterSelector);
  const reportCollectionsFilters = useSelector(reportCollectionsFilterSelector);

  useEffect(() => {
    handler.setExportConfig({
      PDF: {
        generator: (table, title) => {
          dispatch(
            downloadReportPdf({
              locale: handler.locale,
              t,
              table,
              title,
              includeMissingClosingTable: false,
              parameters: ['countryCode', 'stores', 'startBusinessDate', 'endBusinessDate'],
              opStructures: [
                'regionalMgmt',
                'region',
                'management',
                'supervision',
                'agreement',
                'company',
              ],
              filters: getAccountingInformationReportPdfFilters(
                reportFilters,
                reportCollectionsFilters
              ),
              additionalParameters: getRecaPdfParameters(
                t,
                reportFilters,
                reportCollectionsFilters,
                ['differenceType', 'paymentTypes']
              ),
            })
          );
        },
        isDispatchAction: true,
      },
    });
  }, [handler, dispatch, t, reportFilters, reportCollectionsFilters]);

  return null;
};

const PettyCashDifferenceByPaymentTypeDataGrid = () => (
  <DataGridFlexSales handlerFactory={handlerFactory}>
    <DataGridConfig />
    <DataGridExportConfig />
  </DataGridFlexSales>
);

export default PettyCashDifferenceByPaymentTypeDataGrid;
